import { PatternType, PromptDefinitionFields } from '../Services/PromptApiProviderTypes';
import { isEmpty } from '../utils/validationUtil';

// ─── Types  ──────────────────────────────────────────────────

export interface PromptFormState {
  name: string;
  patternType: PatternType | null;
  prompt: string;
  role: string;
  goal: string;
  instruction: string;
  narrowing: string;
  stepsToComplete: string;
  checks: string;
  details: string;
  examples: string;
  objective: string;
  additionalGuidelines: string;
  benchmark: string;
  recursion: string;
  speaker: string;
  listener: string;
  listenerRole: string;
}

export interface PromptPageState {
  loading: boolean;
  error: string | null;
  isNew: boolean;
  isDisabled: boolean;
  isOptimizerOpen: boolean;
}

export type PromptFormAction =
  | { type: 'SET_FIELD'; field: keyof PromptFormState; value: string }
  | { type: 'SET_PATTERN_TYPE'; value: PatternType | null }
  | { type: 'LOAD_PROMPT'; data: Partial<PromptFormState> }
  | { type: 'RESET' };

export type PromptPageAction =
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; value: string | null }
  | { type: 'SET_IS_NEW'; value: boolean }
  | { type: 'SET_IS_DISABLED'; value: boolean }
  | { type: 'SET_OPTIMIZER_OPEN'; value: boolean };

type AlertFn = (type: string, message: string) => void;

interface ValidationRule {
  field: keyof PromptFormState;
  message: string;
}

// ─── Utils ──────────────────────────────────────────────────

const labeled = (label: string, value: string | undefined): string | null =>
  value ? `[${label}]\n${value}` : null;

export function getOptimizePromptText(form: PromptFormState): string {
  const { patternType } = form;

  const sections: Record<PatternType, () => (string | null)[]> = {
    simple_prompt: () => [form.prompt || null],
    rodes: () => [
      labeled('ROLE', form.role),
      labeled('OBJECTIVE', form.objective),
      labeled('DETAILS', form.details),
      labeled('EXAMPLES', form.examples),
      labeled('CHECKS', form.checks),
    ],
    risen: () => [
      labeled('ROLE', form.role),
      labeled('INSTRUCTION', form.instruction),
      labeled('GOAL', form.goal),
      labeled('STEPS TO COMPLETE', form.stepsToComplete),
      labeled('NARROWING', form.narrowing),
    ],
    chain_of_density: () => [
      labeled('INSTRUCTION', form.instruction),
      labeled('RECURSION', form.recursion),
      labeled('BENCHMARK', form.benchmark),
      labeled('ADDITIONAL GUIDELINES', form.additionalGuidelines),
    ],
    swarm: () => [labeled('INSTRUCTION', form.instruction), labeled('ROLE', form.role)],
    realtime: () => [
      labeled('SPEAKER', form.speaker),
      labeled('LISTENER_ROLE', form.listenerRole),
      labeled('LISTENER', form.listener),
    ],
  };

  if (!patternType || !sections[patternType]) return '';

  return sections[patternType]().filter(Boolean).join('\n\n');
}

const VALIDATION_RULES: Record<string, ValidationRule[]> = {
  simple_prompt: [{ field: 'prompt', message: 'Please enter a prompt.' }],
  rodes: [
    { field: 'role', message: 'Please enter a role.' },
    { field: 'objective', message: 'Please enter an objective.' },
    { field: 'details', message: 'Please enter details.' },
    { field: 'examples', message: 'Please enter examples.' },
    { field: 'checks', message: 'Please enter checks.' },
  ],
  risen: [
    { field: 'role', message: 'Please enter a role.' },
    { field: 'instruction', message: 'Please enter an instruction.' },
    { field: 'stepsToComplete', message: 'Please enter steps to complete.' },
    { field: 'goal', message: 'Please enter a goal.' },
    { field: 'narrowing', message: 'Please enter a narrowing.' },
  ],
  chain_of_density: [
    { field: 'instruction', message: 'Please enter an instruction.' },
    { field: 'recursion', message: 'Please enter a recursion.' },
    { field: 'benchmark', message: 'Please enter a benchmark.' },
    { field: 'additionalGuidelines', message: 'Please enter additional guidelines.' },
  ],
  swarm: [
    { field: 'instruction', message: 'Please enter an instruction.' },
    { field: 'role', message: 'Please enter a role.' },
  ],
  realtime: [
    { field: 'speaker', message: 'Please enter a speaker.' },
    { field: 'listenerRole', message: 'Please enter a listener role.' },
    { field: 'listener', message: 'Please enter a listener.' },
  ],
};

export function validateForm(form: PromptFormState, showAlert: AlertFn): boolean {
  if (isEmpty(form.name)) {
    showAlert('fail', 'Please enter a name for the prompt.');
    return false;
  }
  if (isEmpty(form.patternType)) {
    showAlert('fail', 'Please select a prompt pattern.');
    return false;
  }

  const rules = VALIDATION_RULES[form.patternType!];
  if (rules) {
    for (const rule of rules) {
      if (isEmpty(form[rule.field])) {
        showAlert('fail', rule.message);
        return false;
      }
    }
  }

  return true;
}

export function buildDefinition(form: PromptFormState): PromptDefinitionFields {
  const definition: PromptDefinitionFields = {};

  switch (form.patternType) {
    case 'simple_prompt':
      definition.prompt = form.prompt;
      break;
    case 'rodes':
      definition.role = form.role;
      definition.objective = form.objective;
      definition.details = form.details;
      definition.examples = form.examples;
      definition.checks = form.checks;
      break;
    case 'risen':
      definition.role = form.role;
      definition.instruction = form.instruction;
      definition.goal = form.goal;
      definition.steps_to_complete = form.stepsToComplete;
      definition.narrowing = form.narrowing;
      break;
    case 'chain_of_density':
      definition.instruction = form.instruction;
      definition.recursion = form.recursion;
      definition.benchmark = form.benchmark;
      definition.additional_guidelines = form.additionalGuidelines;
      break;
    case 'swarm':
      definition.instruction = form.instruction;
      definition.role = form.role;
      break;
    case 'realtime':
      definition.speaker = form.speaker;
      definition.listener_role = form.listenerRole;
      definition.listener = form.listener;
      break;
  }

  return definition;
}

export function mapResponseToFormData(response: any): Partial<PromptFormState> {
  const def = response.definition || {};
  return {
    name: response.name ?? '',
    patternType: response.pattern_type ?? null,
    prompt: def.prompt ?? '',
    role: def.role ?? '',
    objective: def.objective ?? '',
    details: def.details ?? '',
    examples: def.examples ?? '',
    checks: def.checks ?? '',
    instruction: def.instruction ?? '',
    narrowing: def.narrowing ?? '',
    stepsToComplete: def.steps_to_complete ?? '',
    additionalGuidelines: def.additional_guidelines ?? '',
    benchmark: def.benchmark ?? '',
    recursion: def.recursion ?? '',
    goal: def.goal ?? '',
    speaker: def.speaker ?? '',
    listenerRole: def.listener_role ?? '',
    listener: def.listener ?? '',
  };
}
